// ============================================
// Consent Enforcement Utility
// Checks if user has active consent for a given
// purpose before data processing occurs.
// ============================================

import { getConsentBySession } from '../services/firestore/dataopsService';
import type { ConsentPurpose } from '../types/dataops';

/** Consent check result */
export interface ConsentCheck {
  hasConsent: boolean;
  purpose: ConsentPurpose;
  grantedAt: string | null;
}

/**
 * Check if a session has active consent for the given purpose.
 * Returns the latest consent state (granted vs withdrawn).
 */
export async function checkConsent(
  sessionId: string,
  purpose: ConsentPurpose,
): Promise<ConsentCheck> {
  const records = await getConsentBySession(sessionId);
  const latest = records.find((r) => r.purpose === purpose);

  return {
    hasConsent: latest?.action === 'granted',
    purpose,
    grantedAt: latest?.action === 'granted' ? latest.timestamp : null,
  };
}

/**
 * Check multiple consent purposes at once.
 * Returns a map of purpose → consent status.
 */
export async function checkConsents(
  sessionId: string,
  purposes: ConsentPurpose[],
): Promise<Record<ConsentPurpose, ConsentCheck>> {
  const records = await getConsentBySession(sessionId);
  const result = {} as Record<ConsentPurpose, ConsentCheck>;

  for (const purpose of purposes) {
    const latest = records.find((r) => r.purpose === purpose);
    result[purpose] = {
      hasConsent: latest?.action === 'granted',
      purpose,
      grantedAt: latest?.action === 'granted' ? latest.timestamp : null,
    };
  }

  return result;
}

/**
 * Verify all required consents are granted before processing.
 * Throws if any required consent is missing.
 */
export async function enforceRequiredConsents(
  sessionId: string,
): Promise<void> {
  const required: ConsentPurpose[] = ['lab_data_collection', 'health_profile'];
  const checks = await checkConsents(sessionId, required);

  const missing = required.filter((p) => !checks[p].hasConsent);
  if (missing.length > 0) {
    throw new ConsentError(
      `Missing required consent for: ${missing.join(', ')}`,
      missing,
    );
  }
}

/** Error thrown when consent enforcement fails. */
export class ConsentError extends Error {
  constructor(
    message: string,
    public readonly missingPurposes: ConsentPurpose[],
  ) {
    super(message);
    this.name = 'ConsentError';
  }
}
